import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { useUnits } from "@/hooks/useUnits"
import { useCreateFinancialRevenue } from "@/hooks/useFinancialRevenues"

const revenueSchema = z.object({
  description: z.string().min(1, "Descrição é obrigatória"),
  amount_cents: z.number().min(1, "Valor deve ser maior que zero"),
  category: z.string().min(1, "Categoria é obrigatória"),
  revenue_date: z.string().min(1, "Data é obrigatória"),
  unit_id: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional()
})

type RevenueFormData = z.infer<typeof revenueSchema>

interface RegistrarReceitaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const RegistrarReceitaModal = ({ open, onOpenChange }: RegistrarReceitaModalProps) => {
  const { data: units = [] } = useUnits()
  const createRevenue = useCreateFinancialRevenue()

  const form = useForm<RevenueFormData>({
    resolver: zodResolver(revenueSchema),
    defaultValues: {
      description: "",
      amount_cents: 0,
      category: "outros",
      revenue_date: new Date().toISOString().split('T')[0],
      unit_id: "",
      payment_method: "",
      notes: ""
    }
  })

  const onSubmit = async (data: RevenueFormData) => {
    try {
      await createRevenue.mutateAsync({
        ...data,
        unit_id: data.unit_id || undefined,
        payment_method: data.payment_method || undefined,
        notes: data.notes || undefined,
        currency: 'BRL'
      } as any)
      onOpenChange(false)
      form.reset()
    } catch (error) {
      console.error("Erro ao registrar receita:", error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Registrar Receita</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Pagamento taxa de condomínio - Janeiro 2024" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount_cents"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor (R$) *</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.01" 
                        placeholder="0.00"
                        onChange={(e) => field.onChange(Math.round(parseFloat(e.target.value || "0") * 100))}
                        value={field.value ? (field.value / 100).toFixed(2) : ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="revenue_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data da Receita *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoria *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="condominio">Taxa de Condomínio</SelectItem>
                      <SelectItem value="multa">Multa</SelectItem>
                      <SelectItem value="aluguel_area_comum">Aluguel de Área Comum</SelectItem>
                      <SelectItem value="estacionamento">Estacionamento</SelectItem>
                      <SelectItem value="contribuicao_extra">Contribuição Extraordinária</SelectItem>
                      <SelectItem value="juros">Juros</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="unit_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unidade (opcional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma unidade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.building && `${unit.building} - `}Unidade {unit.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Forma de Recebimento</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="pix">PIX</SelectItem>
                      <SelectItem value="ted">TED</SelectItem>
                      <SelectItem value="boleto">Boleto</SelectItem>
                      <SelectItem value="cartao">Cartão</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Informações adicionais sobre a receita"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={createRevenue.isPending}>
                {createRevenue.isPending ? "Registrando..." : "Registrar Receita"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
